import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Download,
  Printer,
  Truck,
  ShieldCheck,
  Heart,
  Eye,
  X,
  Search,
  CheckCircle2,
  Calendar,
  User,
  CreditCard,
  FileCheck2,
} from "lucide-react";
import { toast } from "sonner";

import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { formatINR } from "@/project/data/sample";
import { useDonations, useOrganisations } from "@/project/lib/queries";
import { downloadCsv, printReceipt } from "@/project/lib/receipt";

export const Route = createFileRoute("/admin/donations")({
  component: AdminDonations,
});

function AdminDonations() {
  const { data: donations = [] } = useDonations();
  const { data: orgs = [] } = useOrganisations({ all: true });
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState<"all" | "monetary" | "inkind">(
    "all"
  );
  const [viewingDonation, setViewingDonation] = useState<any | null>(null);

  const orgName = (id: string | null) =>
    orgs.find((o) => o.id === id)?.name ?? "Shree Ahmedabad Panjrapole";

  const filtered = donations.filter((d: any) => {
    const matchesSearch = [
      d.donor_name,
      d.donor_email,
      d.receipt_no,
      d.target_label,
      d.item_name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q.toLowerCase());

    const isKind = d.donation_type === "inkind" || !!d.item_name;
    if (filterType === "monetary" && isKind) return false;
    if (filterType === "inkind" && !isKind) return false;
    return matchesSearch;
  });

  const totalMonetary = donations.reduce(
    (s: number, d: any) => s + (d.amount || 0),
    0
  );
  const inkindCount = donations.filter(
    (d: any) => d.donation_type === "inkind" || !!d.item_name
  ).length;

  return (
    <>
      <PageHead
        title="Donation Register & Audit"
        subtitle="Audit 80G tax-exempt monetary gifts and track physical In-Kind material dispatches."
        action={
          <button
            onClick={() =>
              downloadCsv("donations-register", [
                [
                  "Receipt No",
                  "Date",
                  "Donor Name",
                  "Type",
                  "Item/Purpose",
                  "Amount",
                  "Organisation",
                  "Dispatch Status",
                  "PAN",
                ],
                ...donations.map((d: any) => [
                  d.receipt_no ?? d.id,
                  new Date(d.created_at ?? Date.now()).toLocaleDateString(
                    "en-IN"
                  ),
                  d.anonymous ? "Anonymous" : d.donor_name,
                  d.donation_type ?? "monetary",
                  d.item_name
                    ? `${d.quantity ?? ""} ${d.item_name}`
                    : d.target_label ?? "Seva Fund",
                  d.amount,
                  orgName(d.org_id),
                  d.dispatch_status ?? "Paid",
                  d.donor_pan ?? "",
                ]),
              ])
            }
            className="flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-xs font-bold text-foreground hover:bg-[var(--cream-2)] transition shadow-xs cursor-pointer"
          >
            <Download className="h-3.5 w-3.5" /> Export Register CSV
          </button>
        }
      />

      {/* Production-Ready KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="rounded-2xl border border-border/80 bg-white p-5 shadow-xs relative overflow-hidden border-t-4 border-t-slate-700">
          <p className="text-xs uppercase tracking-wider text-foreground/60 font-bold">Total Donations</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-foreground">{donations.length}</p>
        </div>
        <div className="rounded-2xl border border-border/80 bg-white p-5 shadow-xs relative overflow-hidden border-t-4 border-t-emerald-600">
          <p className="text-xs uppercase tracking-wider text-foreground/60 font-bold">Total Value Raised</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-emerald-700">{formatINR(totalMonetary)}</p>
        </div>
        <div className="rounded-2xl border border-border/80 bg-white p-5 shadow-xs relative overflow-hidden border-t-4 border-t-[var(--saffron-deep)]">
          <p className="text-xs uppercase tracking-wider text-foreground/60 font-bold">In-Kind Material Sevas</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-[var(--saffron-deep)]">{inkindCount}</p>
        </div>
        <div className="rounded-2xl border border-border/80 bg-white p-5 shadow-xs relative overflow-hidden border-t-4 border-t-amber-600">
          <p className="text-xs uppercase tracking-wider text-foreground/60 font-bold">80G Receipts Issued</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-foreground">{donations.length}</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Filter Controls Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-border/80 shadow-xs">
          <div className="flex rounded-xl bg-[var(--cream-2)] p-1 text-xs border border-border/60">
            <button
              onClick={() => setFilterType("all")}
              className={`rounded-lg px-4 py-2 font-bold transition cursor-pointer ${
                filterType === "all"
                  ? "bg-white shadow-xs text-foreground border border-border/60"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              All Seva ({donations.length})
            </button>
            <button
              onClick={() => setFilterType("monetary")}
              className={`rounded-lg px-4 py-2 font-bold transition cursor-pointer ${
                filterType === "monetary"
                  ? "bg-slate-800 text-white shadow-xs"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              Rupee 80G ({donations.length - inkindCount})
            </button>
            <button
              onClick={() => setFilterType("inkind")}
              className={`rounded-lg px-4 py-2 font-bold transition cursor-pointer ${
                filterType === "inkind"
                  ? "bg-[var(--saffron)] text-foreground shadow-xs"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              In-Kind Material ({inkindCount})
            </button>
          </div>

          <div className="flex items-center gap-2 rounded-full border border-border/80 bg-white px-4 py-2 text-xs w-full sm:w-80 shadow-xs">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search donor, receipt no, fodder item..."
              className="bg-transparent outline-none w-full placeholder:text-muted-foreground text-xs"
            />
          </div>
        </div>

        {/* Enhanced Production-Ready Table */}
        <Panel title="Donation Audit Ledger">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[950px] text-sm">
              <thead className="text-xs uppercase tracking-wider text-foreground/70 font-bold border-b border-border bg-[var(--cream-2)]">
                <tr className="text-left">
                  <th className="py-3.5 px-4">Receipt No</th>
                  <th className="px-4">Donor</th>
                  <th className="px-4">Type</th>
                  <th className="px-4">Item / Purpose</th>
                  <th className="px-4">Beneficiary Gaushala</th>
                  <th className="px-4">Amount</th>
                  <th className="px-4">Dispatch Status</th>
                  <th className="text-right px-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-xs font-medium">
                {filtered.map((d: any) => {
                  const isInKind =
                    d.donation_type === "inkind" || !!d.item_name;
                  return (
                    <tr
                      key={d.id}
                      className="hover:bg-[var(--cream-2)]/80 transition-colors"
                    >
                      <td className="py-3.5 px-4 font-mono font-extrabold text-foreground">
                        {d.receipt_no ?? d.id.slice(0, 12)}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-bold text-foreground block text-sm">
                          {d.anonymous ? "Anonymous Sevak" : d.donor_name}
                        </span>
                        <span className="text-[11px] text-foreground/60 font-normal">
                          {d.donor_email || d.donor_phone || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold ${
                            isInKind
                              ? "bg-amber-100 text-amber-900 border border-amber-300"
                              : "bg-slate-100 text-slate-800 border border-slate-300"
                          }`}
                        >
                          {isInKind ? (
                            <Truck className="h-3.5 w-3.5 text-amber-700" />
                          ) : (
                            <Heart className="h-3.5 w-3 text-slate-700 fill-current" />
                          )}
                          {isInKind ? "In-Kind Material" : "Cash 80G"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 max-w-xs font-semibold text-foreground/90 leading-snug">
                        {isInKind
                          ? `${d.quantity ?? ""} ${d.item_name}`
                          : d.target_label ?? "General Seva Fund"}
                      </td>
                      <td className="px-4 py-3.5 font-bold text-foreground">
                        {orgName(d.org_id)}
                      </td>
                      <td className="px-4 py-3.5 font-extrabold text-sm text-foreground">
                        {formatINR(d.amount)}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-700 border border-emerald-200">
                          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />{" "}
                          {d.dispatch_status ?? "Paid"}
                        </span>
                      </td>
                      <td className="text-right px-4 py-3.5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setViewingDonation(d)}
                            title="View Receipt Info"
                            className="grid h-8 w-8 place-items-center rounded-xl border border-border/80 bg-white hover:bg-[var(--cream-2)] text-foreground/80 transition shadow-xs cursor-pointer"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              printReceipt({
                                id:
                                  d.receipt_no ??
                                  d.id.slice(0, 10).toUpperCase(),
                                date: new Date(
                                  d.created_at ?? Date.now()
                                ).toLocaleDateString("en-IN"),
                                donor: d.anonymous
                                  ? "Anonymous Sevak"
                                  : d.donor_name,
                                pan: d.donor_pan || undefined,
                                amount: d.amount,
                                purpose: d.target_label ?? "General Seva Fund",
                                beneficiary: orgName(d.org_id),
                                frequency: d.frequency,
                                donationType: d.donation_type,
                                itemName: d.item_name,
                                quantity: d.quantity,
                                dispatchStatus: d.dispatch_status,
                              })
                            }
                            className="inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-white px-3.5 py-1.5 text-xs font-bold hover:bg-[var(--cream-2)] transition shadow-xs text-foreground cursor-pointer"
                          >
                            <Printer className="h-3.5 w-3.5" /> 80G PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* ── View Donation Details Modal ───────────────────────────────── */}
      {viewingDonation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in"
          onClick={() => setViewingDonation(null)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white border border-border p-6 shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <FileCheck2 className="h-5 w-5 text-foreground" />
                <h3 className="font-display text-base font-bold text-foreground">
                  Donation Audit Record
                </h3>
              </div>
              <button
                onClick={() => setViewingDonation(null)}
                className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-muted text-muted-foreground cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="rounded-2xl bg-[var(--cream-2)] p-4 space-y-2 border border-border/60 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">Receipt No</span>
                <span className="font-mono font-bold text-foreground">
                  {viewingDonation.receipt_no ?? viewingDonation.id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">Donor Name</span>
                <span className="font-bold text-foreground">
                  {viewingDonation.donor_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">Donor Email / Phone</span>
                <span className="font-medium text-foreground">
                  {viewingDonation.donor_email || viewingDonation.donor_phone || "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">PAN Number (80G)</span>
                <span className="font-mono font-bold uppercase text-foreground">
                  {viewingDonation.donor_pan || "ABCDE1234F"}
                </span>
              </div>
              <div className="flex justify-between border-t border-border/60 pt-2">
                <span className="text-muted-foreground font-semibold">Amount</span>
                <span className="font-extrabold text-base text-foreground">
                  {formatINR(viewingDonation.amount)}
                </span>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={() => setViewingDonation(null)}
                className="rounded-full bg-slate-900 px-5 py-2 text-xs font-bold text-white hover:bg-slate-800 transition cursor-pointer"
              >
                Close Audit Record
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}